import {
    add,
    Days,
    Euros,
    Factor,
    Hours,
    Meals,
    min,
    round,
    multiplyWithFactor,
    multiplyWithPercent,
    multiplyWithRate,
    Percent,
    Rate,
    sum,
} from "@pentacode/openapi";
import {
    AbsenceMode,
    BonusTypeMode,
    BreakTiming,
    Company,
    CostCenter,
    Employee,
    TimeEntry,
    TimeEntryType,
    TimeSettings,
} from "../model";
import { getAncillaryCostFactor, getHourlyRate } from "../payroll";
import { getSalaryOrDefault } from "../contract.util";
import { calculateBreakDurations } from "../breaks";
import { BonusResult, getBonusResults } from "./bonus-result";
import { getIntervalDuration, Interval } from "./interval";
import { hour, week } from "./consts";

export type TimeResultMode = "final" | "planned" | "logged" | "mixed" | "max";

type MealStats = {
    count: Meals;
    value: Rate<Euros, Meals>;
};

type MealStatsByType = {
    breakfast?: MealStats;
    lunch?: MealStats;
    dinner?: MealStats;
};

type MealCost = MealStats & {
    costs: Euros;
};

type MealsResult = {
    breakfast?: MealCost;
    lunch?: MealCost;
    dinner?: MealCost;
};

type BaseTimeResult = {
    duration: Hours;
    hourlyRate: Rate<Euros, Hours>;
    wages: Euros;
    ancillaryCosts: Euros;
};

type BreaksTimeResult = {
    duration: Hours;
    paidDuration: Hours;
    hourlyRate: Rate<Euros, Hours>;
    wages: Euros;
    ancillaryCosts: Euros;
};

type CommissionTimeResult = {
    revenue: Euros;
    percent: Percent;
    wages: Euros;
    ancillaryCosts: Euros;
};

export type TimeResult = {
    base: BaseTimeResult;
    breaks: BreaksTimeResult;
    commission?: CommissionTimeResult;
    bonuses: BonusResult[];
    meals: MealsResult;
    days: Days;
    totalWages: Euros;
    totalCosts: Euros;
    costCenter?: {
        number: string;
        name: string;
    };
};

function calculateMealCost(meal?: MealStats): MealCost | undefined {
    const costs = (meal && multiplyWithRate(meal.count, meal.value)) || (0 as Euros);

    return meal
        ? {
              costs,
              ...meal,
          }
        : undefined;
}

export function makeMealResult(meals: MealStatsByType): MealsResult {
    return {
        breakfast: calculateMealCost(meals.breakfast),
        lunch: calculateMealCost(meals.lunch),
        dinner: calculateMealCost(meals.dinner),
    };
}

export function sumMeals(meals: MealsResult): Euros {
    return sum(
        meals.breakfast?.costs || (0 as Euros),
        meals.lunch?.costs || (0 as Euros),
        meals.dinner?.costs || (0 as Euros)
    );
}

export function makeEmptyTimeResult(): TimeResult {
    return {
        base: {
            duration: 0 as Hours,
            hourlyRate: 0 as Rate<Euros, Hours>,
            wages: 0 as Euros,
            ancillaryCosts: 0 as Euros,
        },
        breaks: {
            duration: 0 as Hours,
            paidDuration: 0 as Hours,
            hourlyRate: 0 as Rate<Euros, Hours>,
            wages: 0 as Euros,
            ancillaryCosts: 0 as Euros,
        },
        bonuses: [],
        meals: {},
        days: 0 as Days,
        totalWages: 0 as Euros,
        totalCosts: 0 as Euros,
    };
}

type HourAdjustmentTimeResultInput = {
    baseDuration: Hours;
    hourlyRate: Rate<Euros, Hours>;
};

type WorkTimeResultInput = {
    baseDuration: Hours;
    breakPaid: Hours;
    breakTotal: Hours;
    bonuses: BonusResult[];
    revenue: Euros;
    hourlyRate: Rate<Euros, Hours>;
    ancillaryCostFactor: Factor;
    days: Days;
    commission?: Percent;
    costCenter?: CostCenter;
    meals: MealStatsByType;
};

type AbsenceTimeResultInput = {
    baseDuration: Hours;
    breakPaid: Hours;
    breakTotal: Hours;
    bonuses: BonusResult[];
    hourlyRate: Rate<Euros, Hours>;
    ancillaryCostFactor: Factor;
    days: Days;
    costCenter?: CostCenter;
};

type VacationAdjustmentTimeResultInput = {
    baseDuration: Hours;
    breakPaid: Hours;
    breakTotal: Hours;
    hourlyRate: Rate<Euros, Hours>;
    days: Days;
};

//TODO: here, we calculate wages with high precision, should we adapt to bonus calculation?
export function calculateBaseTimeResult(
    duration: Hours,
    hourlyRate: Rate<Euros, Hours>,
    ancillaryCostFactor: Factor
): BaseTimeResult {
    const wages = multiplyWithRate(duration, hourlyRate);
    const ancillaryCosts = multiplyWithFactor(wages, ancillaryCostFactor);

    return {
        duration,
        hourlyRate,
        wages,
        ancillaryCosts,
    };
}

export function calculateBreakTimeResult(
    breakPaid: Hours,
    breakTotal: Hours,
    hourlyRate: Rate<Euros, Hours>,
    ancillaryCostFactor: Factor
): BreaksTimeResult {
    const wages = multiplyWithRate(breakPaid, hourlyRate);
    const ancillaryCosts = multiplyWithFactor(wages, ancillaryCostFactor);

    return {
        duration: breakTotal,
        paidDuration: breakPaid,
        hourlyRate,
        wages,
        ancillaryCosts,
    };
}

function makeHourAdjustmentResult(input: HourAdjustmentTimeResultInput): TimeResult {
    const base = calculateBaseTimeResult(
        input.baseDuration,
        input.hourlyRate,
        0 as Factor // No ancillary costs for hour adjustments
    );

    const breaks = calculateBreakTimeResult(
        0 as Hours, // No breaks for hour adjustments
        0 as Hours, // No breaks for hour adjustments
        input.hourlyRate,
        0 as Factor // No ancillary costs for hour adjustments
    );

    return {
        base,
        breaks,
        bonuses: [],
        meals: {},
        days: 0 as Days,
        totalWages: base.wages,
        totalCosts: base.wages,
    };
}

export function makeWorkTimeResult(input: WorkTimeResultInput): TimeResult {
    const base = calculateBaseTimeResult(input.baseDuration, input.hourlyRate, input.ancillaryCostFactor);
    const breaks = calculateBreakTimeResult(
        input.breakPaid,
        input.breakTotal,
        input.hourlyRate,
        input.ancillaryCostFactor
    );

    const bonusWages = input.bonuses.reduce((total: Euros, bonus) => add(total, bonus.wages), 0 as Euros);
    const bonusAncillaryCosts = input.bonuses.reduce((total, bonus) => add(total, bonus.ancillaryCosts), 0 as Euros);

    const commissionWages =
        (input.revenue && input.commission && multiplyWithPercent(input.revenue, input.commission)) || (0 as Euros);
    const commissionCost = multiplyWithFactor(commissionWages, input.ancillaryCostFactor);

    const mealsCost = makeMealResult(input.meals);
    const totalMealsCost = sumMeals(mealsCost);

    const totalWages = sum(base.wages, breaks.wages, bonusWages, commissionWages);
    const totalCosts = sum(
        totalWages,
        base.ancillaryCosts,
        breaks.ancillaryCosts,
        bonusAncillaryCosts,
        totalMealsCost,
        commissionCost
    );

    return {
        meals: mealsCost,
        base,
        breaks,
        commission: commissionWages &&
            input.revenue &&
            input.commission &&
            input.revenue && {
                revenue: input.revenue,
                percent: input.commission,
                wages: commissionWages,
                ancillaryCosts: commissionCost,
            },
        bonuses: input.bonuses,
        days: input.days,
        totalWages,
        totalCosts,
        costCenter: input.costCenter
            ? {
                  number: input.costCenter.number,
                  name: input.costCenter.name,
              }
            : undefined,
    };
}

export function makeAbsenceTimeResult(input: AbsenceTimeResultInput): TimeResult {
    const base = calculateBaseTimeResult(input.baseDuration, input.hourlyRate, input.ancillaryCostFactor);
    const breaks = calculateBreakTimeResult(
        input.breakPaid,
        input.breakTotal,
        input.hourlyRate,
        input.ancillaryCostFactor
    );

    const bonusWages = input.bonuses.reduce((total: Euros, bonus) => add(total, bonus.wages), 0 as Euros);
    const bonusAncillaryCosts = input.bonuses.reduce((total, bonus) => add(total, bonus.ancillaryCosts), 0 as Euros);

    const totalWages = sum(base.wages, breaks.wages, bonusWages);
    const totalCosts = sum(totalWages, base.ancillaryCosts, breaks.ancillaryCosts, bonusAncillaryCosts);

    return {
        base,
        breaks,
        bonuses: input.bonuses,
        days: input.days,
        totalWages,
        totalCosts,
        costCenter: input.costCenter
            ? {
                  number: input.costCenter.number,
                  name: input.costCenter.name,
              }
            : undefined,
        meals: {},
    };
}

function makeVacationAdjustmentResult(input: VacationAdjustmentTimeResultInput): TimeResult {
    const base = calculateBaseTimeResult(
        input.baseDuration,
        input.hourlyRate,
        0 as Factor // No ancillary costs for vacation adjustments
    );
    const breaks = calculateBreakTimeResult(
        input.breakPaid,
        input.breakTotal,
        input.hourlyRate,
        0 as Factor // No ancillary costs for vacation adjustments
    );

    const totalWages = sum(base.wages, breaks.wages);
    const totalCosts = sum(totalWages);

    return {
        base,
        breaks,
        bonuses: [],
        meals: {},
        days: input.days,
        totalWages,
        totalCosts,
    };
}

export function makeTimeResult({
    baseDuration,
    breakPaid = 0 as Hours,
    breakTotal = 0 as Hours,
    revenue,
    commission,
    bonuses = [],
    days = 0 as Days,
    meals = {},
    hourlyRate,
    ancillaryCostFactor,
    costCenter,
}: {
    baseDuration: Hours;
    breakPaid?: Hours;
    breakTotal?: Hours;
    bonuses?: BonusResult[];
    days?: TimeResult["days"];
    meals?: MealStatsByType;
    hourlyRate: Rate<Euros, Hours>;
    revenue?: Euros;
    commission?: Percent;
    ancillaryCostFactor: Factor;
    costCenter?: CostCenter;
}): TimeResult {
    const base = calculateBaseTimeResult(baseDuration, hourlyRate, ancillaryCostFactor);
    const breaks = calculateBreakTimeResult(breakPaid, breakTotal, hourlyRate, ancillaryCostFactor);

    const bonusWages = bonuses.reduce((total: Euros, bonus) => add(total, bonus.wages), 0 as Euros);
    const bonusAncillaryCosts = bonuses.reduce((total, bonus) => add(total, bonus.ancillaryCosts), 0 as Euros);

    const commissionWages = (revenue && commission && multiplyWithPercent(revenue, commission)) || (0 as Euros);
    const commissionCost = multiplyWithFactor(commissionWages, ancillaryCostFactor);

    const mealsResult = makeMealResult(meals);
    const totalMealsCost = sumMeals(mealsResult);

    const totalWages = sum(base.wages, breaks.wages, bonusWages, commissionWages, commissionWages);
    const totalCosts = sum(
        totalWages,
        base.ancillaryCosts,
        breaks.ancillaryCosts,
        bonusAncillaryCosts,
        totalMealsCost,
        commissionCost
    );

    return {
        meals: mealsResult,
        base,
        breaks,
        commission: commissionWages &&
            revenue &&
            commission &&
            revenue && {
                revenue,
                percent: commission,
                wages: commissionWages,
                ancillaryCosts: commissionCost,
            },
        bonuses,
        days,
        totalWages,
        totalCosts,
        costCenter: costCenter
            ? {
                  number: costCenter.number,
                  name: costCenter.name,
              }
            : undefined,
    };
}

export function addTimeResults(a: TimeResult, b: TimeResult): TimeResult {
    const bonuses: BonusResult[] = a.bonuses.map((b) => ({ ...b }));
    for (const bonus of b.bonuses || []) {
        let existing = bonuses.find(
            (bon) =>
                bon.bonusId === bonus.bonusId && bon.hourlyRate === bonus.hourlyRate && bon.taxFree === bonus.taxFree
        );
        if (!existing) {
            existing = { ...bonus, duration: 0 as Hours, wages: 0 as Euros, ancillaryCosts: 0 as Euros };
            bonuses.push(existing);
        }
        existing.duration = add(existing.duration, bonus.duration);
        existing.wages = add(existing.wages, bonus.wages);
        existing.ancillaryCosts = add(existing.ancillaryCosts, bonus.ancillaryCosts);
    }

    return {
        base: {
            duration: add(a.base.duration, b.base.duration),
            hourlyRate: add(a.base.hourlyRate, b.base.hourlyRate),
            wages: add(a.base.wages, b.base.wages),
            ancillaryCosts: add(a.base.ancillaryCosts, b.base.ancillaryCosts),
        },
        breaks: {
            duration: add(a.breaks.duration, b.breaks.duration),
            paidDuration: add(a.breaks.paidDuration, b.breaks.paidDuration),
            hourlyRate: add(a.breaks.hourlyRate, b.breaks.hourlyRate),
            wages: add(a.breaks.wages, b.breaks.wages),
            ancillaryCosts: add(a.breaks.ancillaryCosts, b.breaks.ancillaryCosts),
        },
        bonuses,
        meals: {
            breakfast:
                a.meals.breakfast || b.meals.breakfast
                    ? {
                          count: add(a.meals.breakfast?.count, b.meals.breakfast?.count),
                          value: multiplyWithFactor(
                              add(a.meals.breakfast?.value, b.meals.breakfast?.value),
                              0.5 as Factor
                          ),
                          costs: add(a.meals.breakfast?.costs, b.meals.breakfast?.costs),
                      }
                    : undefined,
            lunch:
                a.meals.lunch || b.meals.lunch
                    ? {
                          count: add(a.meals.lunch?.count, b.meals.lunch?.count),
                          value: multiplyWithFactor(add(a.meals.lunch?.value, b.meals.lunch?.value), 2 as Factor),
                          costs: add(a.meals.lunch?.costs, b.meals.lunch?.costs),
                      }
                    : undefined,
            dinner:
                a.meals.dinner || b.meals.dinner
                    ? {
                          count: add(a.meals.dinner?.count, b.meals.dinner?.count),
                          value: multiplyWithFactor(add(a.meals.dinner?.value, b.meals.dinner?.value), 2 as Factor),
                          costs: add(a.meals.dinner?.costs, b.meals.dinner?.costs),
                      }
                    : undefined,
        },
        days: add(a.days, b.days),
        totalWages: add(a.totalWages, b.totalWages),
        totalCosts: add(a.totalCosts, b.totalCosts),
    };
}

export function getTotalResults(results: TimeResult[]): TimeResult {
    return results.reduce((total, res) => addTimeResults(total, res), makeEmptyTimeResult());
}

export function getDailyAverage(results: TimeResult[]): TimeResult {
    const total = getTotalResults(results);
    const average = makeEmptyTimeResult();
    const days = total.days;

    if (!days) {
        return total;
    }

    const dayFraction = (1 / days) as Factor;

    average.base.duration = multiplyWithFactor(total.base.duration, dayFraction);
    average.base.wages = multiplyWithFactor(total.base.wages, dayFraction);
    average.base.ancillaryCosts = multiplyWithFactor(total.base.ancillaryCosts, dayFraction);
    average.base.hourlyRate = multiplyWithFactor(total.base.hourlyRate, dayFraction);

    const bonusesByType = results.reduce((bbt, result) => {
        if (!result) {
            return bbt;
        }
        for (const bonus of result.bonuses || []) {
            if (!bbt.has(bonus.type.id)) {
                bbt.set(bonus.type.id, []);
            }
            bbt.get(bonus.type.id)!.push(bonus);
        }
        return bbt;
    }, new Map<number, BonusResult[]>());

    average.bonuses = [...bonusesByType.values()].map((bonuses) => ({
        ...bonuses[0],
        duration: multiplyWithFactor(
            bonuses.reduce((total, bonus) => add(total, bonus.duration), 0 as Hours),
            dayFraction
        ),
        wages: multiplyWithFactor(
            bonuses.reduce((total, bonus) => add(total, bonus.wages), 0 as Euros),
            dayFraction
        ),
        ancillaryCosts: multiplyWithFactor(
            bonuses.reduce((total, bonus) => add(total, bonus.ancillaryCosts), 0 as Euros),
            dayFraction
        ),
        hourlyRate: multiplyWithFactor(
            bonuses.reduce((total, bonus) => add(total, bonus.hourlyRate), 0 as Rate<Euros, Hours>),
            dayFraction
        ),
    }));

    average.meals = {
        breakfast: total.meals.breakfast && {
            count: multiplyWithFactor(total.meals.breakfast.count, dayFraction),
            value: multiplyWithFactor(total.meals.breakfast.value, dayFraction),
            costs: multiplyWithFactor(total.meals.breakfast.costs, dayFraction),
        },
        lunch: total.meals.lunch && {
            count: multiplyWithFactor(total.meals.lunch.count, dayFraction),
            value: multiplyWithFactor(total.meals.lunch.value, dayFraction),
            costs: multiplyWithFactor(total.meals.lunch.costs, dayFraction),
        },
        dinner: total.meals.dinner && {
            count: multiplyWithFactor(total.meals.dinner.count, dayFraction),
            value: multiplyWithFactor(total.meals.dinner.value, dayFraction),
            costs: multiplyWithFactor(total.meals.dinner.costs, dayFraction),
        },
    };

    return average;
}

/** Gets the shift duration for calculating the time result. */
export function getShiftInterval(entry: Readonly<TimeEntry>, mode: TimeResultMode): Interval | null {
    switch (mode) {
        case "planned":
            return entry.planned;
        case "logged":
            return entry.logged;
        case "final":
            return entry.final;
        case "mixed":
            if (entry.final || entry.isPast) {
                return entry.final;
            }
            return entry.planned;
        case "max": {
            return entry.logged && entry.final
                ? [
                      new Date(Math.min(entry.logged[0].getTime(), entry.final[0].getTime())),
                      new Date(Math.max(entry.logged[1].getTime(), entry.final[1].getTime())),
                  ]
                : entry.final;
        }
    }
}

export function getTimeResult(
    company: Company,
    employee: Employee,
    entry: TimeEntry,
    prevAverage: Pick<TimeResult, "base" | "breaks" | "bonuses">,
    shiftsThatDay: number = 1,
    mode: TimeResultMode = "final"
): TimeResult {
    const timeSettings = company.getTimeSettings({ timeEntry: entry }) || new TimeSettings();
    const { iterativeBreaks } = company.settings;

    const mealValueBreakfast = company.settings.mealValueBreakfast;
    const mealValueLunch = company.settings.mealValueLunch;
    const mealValueDinner = company.settings.mealValueDinner;

    const contract = employee.getContractForDate(entry.date);
    const { paidBreaksAuto, paidBreaksManual, breakTiming } = timeSettings;

    const costCenter = company.getCostCenter({ employee, timeEntry: entry, date: entry.date });

    if (!contract) {
        return makeEmptyTimeResult();
    }

    const hourlyRate = getHourlyRate(company, contract, entry.positionId);
    const salary = getSalaryOrDefault(entry.positionId, contract.salaries);
    const commission = salary?.commission;
    const ancillaryCostFactor = getAncillaryCostFactor(contract.employmentType)[0];

    const { absenceMode, absenceHours, fixedWorkDays, hoursPerDay } = contract;

    const year = entry.start.getFullYear();
    const month = entry.start.getMonth();
    const date = entry.start.getDate();

    if (entry.type === TimeEntryType.Work) {
        const shiftinterval = getShiftInterval(entry, mode);
        if (!shiftinterval || !shiftinterval[0] || !shiftinterval[1]) {
            return makeEmptyTimeResult();
        }

        const shiftDuration = getIntervalDuration(shiftinterval);
        const breakDurations = calculateBreakDurations(entry, timeSettings, mode, shiftDuration, iterativeBreaks);

        const breakPaid = add(
            paidBreaksAuto ? breakDurations.auto : (0 as Hours),
            paidBreaksManual ? breakDurations.manual : (0 as Hours)
        );
        const breakTotal = add(breakDurations.auto, breakDurations.manual);

        let [start, end] = shiftinterval;

        // subtract break at start of end of shift, depending on setting
        if (breakTiming === BreakTiming.End) {
            end = new Date(end.getTime() - breakTotal * hour);
        } else {
            start = new Date(start.getTime() + breakTotal * hour);
        }
        const work = [start, end] as Interval;
        const baseDuration = ((end.getTime() - start.getTime()) / hour) as Hours;

        const bonuses = getBonusResults(company, employee, entry, work);

        return makeWorkTimeResult({
            breakPaid,
            breakTotal,
            baseDuration,
            bonuses,
            meals: {
                breakfast: entry.mealsBreakfast
                    ? {
                          count: entry.mealsBreakfast,
                          value: mealValueBreakfast || (0 as Rate<Euros, Meals>),
                      }
                    : undefined,
                lunch: entry.mealsLunch
                    ? {
                          count: entry.mealsLunch,
                          value: mealValueLunch || (0 as Rate<Euros, Meals>),
                      }
                    : undefined,
                dinner: entry.mealsDinner
                    ? {
                          count: entry.mealsDinner,
                          value: mealValueDinner || (0 as Rate<Euros, Meals>),
                      }
                    : undefined,
            },
            commission,
            revenue: entry.revenue,
            hourlyRate,
            ancillaryCostFactor,
            days: (1 / shiftsThatDay) as Days,
            costCenter,
        });
    }

    if (
        [
            TimeEntryType.Vacation,
            TimeEntryType.Sick,
            TimeEntryType.ChildSick,
            TimeEntryType.SickInKUG,
            TimeEntryType.CompDay,
        ].includes(entry.type)
    ) {
        let baseDuration = prevAverage.base.duration;
        let breakTotal = prevAverage.breaks.duration;
        let breakPaid = prevAverage.breaks.paidDuration;

        if (absenceMode === AbsenceMode.FixedDays && fixedWorkDays) {
            baseDuration = hoursPerDay ? hoursPerDay[(entry.start.getDay() + 6) % 7] : (0 as Hours);
            breakTotal = breakPaid = 0 as Hours;
        } else if (absenceMode === AbsenceMode.Fixed) {
            baseDuration = absenceHours;
            breakTotal = breakPaid = 0 as Hours;
        }

        // No continued pay in the first 4 weeks of employment
        const firstContract = employee.contracts.sort((a, b) => Number(a.start) - Number(b.start))[0];
        if (!firstContract || Number(new Date(year, month, date)) - Number(firstContract.start) < 4 * week) {
            const result = makeEmptyTimeResult();
            result.base.duration = baseDuration;
        }

        const bonuses: BonusResult[] = [];

        if (entry.type !== TimeEntryType.CompDay) {
            for (const bonus of contract.bonuses) {
                const bonusType = company.bonusTypes?.find((t) => t.id === bonus.typeId);
                if (bonusType?.continuedPay && bonusType?.matchesDate(entry.date, company.country)) {
                    const prevBonusAverage = prevAverage.bonuses?.find((b) => b.type.id === bonus.typeId);
                    const applicableDuration = min(baseDuration, prevBonusAverage?.duration || (0 as Hours));
                    const duration = calculateBonusDuration(
                        new Date(entry.date),
                        bonusType.mode,
                        applicableDuration,
                        baseDuration
                    );

                    const wages = multiplyWithPercent(multiplyWithRate(round(duration, 2), hourlyRate), bonus.percent);
                    bonuses.push({
                        bonusId: bonus.id,
                        type: {
                            id: bonusType.id,
                            name: bonusType.name,
                            legalType: bonusType.legalType,
                        },
                        duration,
                        taxFree: false,
                        percent: bonus.percent,
                        hourlyRate,
                        wages,
                        ancillaryCosts: multiplyWithFactor(wages, ancillaryCostFactor),
                    });
                }
            }
        }

        return makeAbsenceTimeResult({
            baseDuration,
            breakPaid,
            breakTotal,
            bonuses,
            hourlyRate,
            ancillaryCostFactor,
            days: 1 as Days,
            costCenter,
        });
    }

    if (entry.type === TimeEntryType.HourAdjustment) {
        return makeHourAdjustmentResult({
            baseDuration: entry.hours ?? (0 as Hours),
            hourlyRate: entry.paid ? hourlyRate : (0 as Rate<Euros, Hours>),
        });
    }

    if (entry.type === TimeEntryType.VacationAdjustment) {
        const days = (entry.days || 0) as Factor;
        let baseDuration = prevAverage.base.duration;
        let breakTotal = prevAverage.breaks.duration;
        let breakPaid = prevAverage.breaks.paidDuration;

        if (absenceMode === AbsenceMode.FixedDays && fixedWorkDays) {
            baseDuration = hoursPerDay ? hoursPerDay[(entry.start.getDay() + 6) % 7] : (0 as Hours);
            breakTotal = breakPaid = 0 as Hours;
        } else if (absenceMode === AbsenceMode.Fixed) {
            baseDuration = absenceHours;
            breakTotal = breakPaid = 0 as Hours;
        }

        return makeVacationAdjustmentResult({
            baseDuration: multiplyWithFactor(baseDuration, days),
            hourlyRate: entry.paid ? hourlyRate : (0 as Rate<Euros, Hours>),
            breakTotal: multiplyWithFactor(breakTotal, days),
            breakPaid: multiplyWithFactor(breakPaid, days),
            days: entry.days || (0 as Days),
        });
    }

    return makeEmptyTimeResult();
}

export function calculateBonusDuration(
    date: Date,
    bonusTypeMode: BonusTypeMode,
    applicableDuration: Hours,
    baseDuration: Hours
) {
    if (bonusTypeMode === BonusTypeMode.Daily) {
        // always use the 13-week average for daily bonuses
        return applicableDuration;
    } else if (bonusTypeMode === BonusTypeMode.WeekDays && date.getDay() !== 0) {
        // for week day bonuses (except sundays) use the 13-week average
        return applicableDuration;
    }

    // use base duration for sundays, holidays and single dates (like 24.12.)
    return baseDuration;
}
